import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {
  areCompetitorResultsEqual,
  CategoryType,
  CompetitorResults,
  EventItem,
  Lookups,
  Race,
  RaceSettings,
  ResultProcessingType
} from "@model";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {ResultsDetailsModalComponent} from "../results-details-modal/results-details-modal.component";
import {AbstractComponent} from "../abstract.component";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";

@Component({
  selector: 'app-live-results-table-body',
  templateUrl: './live-results-table-body.component.html',
  styleUrls: ['./live-results-table-body.component.scss'],
  animations: [
    trigger('animate', [
      state('*', style({transform: 'scaleY(1)', opacity: 1})),

      transition('void => *', [
        style({transform: 'translateY(100%)', opacity: 0}),
        animate(500),
      ]),
    ]),
  ],
})
export class LiveResultsTableBodyComponent extends AbstractComponent implements OnChanges {

  static readonly PAGE_SIZE = 7;
  static readonly CAROUSEL_INTERVAL = 6000;

  @Input() competitorLiveResults: CompetitorResults[] = [];
  @Input() race: Race | undefined;
  @Input() event: EventItem | undefined = undefined;
  @Input({required: true}) languages: Lookups[] = [];
  @Input() isRoundRace?: boolean;
  @Input() measurementType: number | undefined = undefined;
  @Input() gender?: number;
  @Input() category?: number;
  @Input() raceSettings: RaceSettings | undefined
  @Input() isFullScreen: boolean = false;
  @Input() isFinished: boolean = false;
  @Input() isCarousel = false;

  previousIndexes: Map<number, { index: number }> = new Map();
  firstCompetitorResults: CompetitorResults | undefined = undefined;
  tableData: LiveResultTableRowData[] = [];
  fixedRows: LiveResultTableRowData[] = [];
  pages: LiveResultTableRowData[][] = [];
  fireAnimate: boolean = false;
  showCategories: boolean = false;
  pageSize = LiveResultsTableBodyComponent.PAGE_SIZE;
  carouselInterval = LiveResultsTableBodyComponent.CAROUSEL_INTERVAL;
  pauseOnHover = false;
  pauseOnFocus = false;
  wrap = true;

  constructor(private modalService: NgbModal) {
    super();
  }

  trackById = (_: number, row: LiveResultTableRowData) => row?.id ?? _;

  ngOnInit(): void {
    this.showCategories = !!this.raceSettings?.category?.categoryType && (this.raceSettings?.category?.categoryType !== CategoryType.NOT_USED);
    if (this.isCarousel) {
      this.carouselInterval = ((this.raceSettings?.rotatingResultsSeconds ?? 0) * 1000) ?? LiveResultsTableBodyComponent.CAROUSEL_INTERVAL;
    }
    this.rebuildPages();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes['competitorLiveResults']?.currentValue) return;
    this.handleCompetitorResultsChange(changes['competitorLiveResults'].previousValue ?? [], changes['competitorLiveResults'].currentValue)
  }

  handleCompetitorResultsChange(previous: CompetitorResults[], current: CompetitorResults[]) {
    const newTableData: LiveResultTableRowData[] = [];

    current.forEach((result, index) => {
      const rowData = new LiveResultTableRowData(0, false, result.id, result);
      const prevIndex = this.previousIndexes.get(result.id!)?.index;
      if (prevIndex !== undefined && this.tableData[prevIndex]) {
        const prevResult = previous[prevIndex];

        if (!areCompetitorResultsEqual(prevResult, result)) {
          this.tableData[prevIndex].updated = true;
        }

        if (prevIndex !== index) {
          this.tableData[prevIndex].delta = (index - prevIndex) * 100;
        }
      }

      this.previousIndexes.set(result.id!, {index});
      newTableData.push(rowData);
    });

    this.fireAnimate = true;
    setTimeout(() => {
      this.fireAnimate = false;
      this.tableData = [...newTableData];
      this.firstCompetitorResults = this.tableData[0]?.competitorResults ?? undefined;
      this.rebuildPages();
    }, 500);
  }

  openDetailsModal(competitorResults: CompetitorResults | undefined, isMobile: boolean = false): void {
    if (competitorResults) {
      const modalRef = this.modalService.open(ResultsDetailsModalComponent, {
        centered: true,
        windowClass: isMobile ? 'results-details-modal modal-mobile' : 'results-details-modal',
      });
      modalRef.componentInstance.competitorResults = competitorResults;
      modalRef.componentInstance.firstCompetitorResults = this.firstCompetitorResults;
      modalRef.componentInstance.measurementType = this.measurementType;
      modalRef.componentInstance.category = this.category;
      modalRef.componentInstance.gender = this.gender;
      modalRef.componentInstance.isRoundRace = this.isRoundRace;
      modalRef.componentInstance.showDistance = this.race?.raceSettings?.resultProcessingType === ResultProcessingType.TIME_LIMITED;
      modalRef.componentInstance.race = this.race;
      modalRef.componentInstance.event = this.event;
      modalRef.componentInstance.languages = this.languages;
      modalRef.componentInstance.isAdmin = false;
    }
  }

  private rebuildPages(): void {
    if (!this.isCarousel) {
      this.fixedRows = [];
      this.pages = [];
      return;
    }
    const rows = this.tableData ?? [];
    this.fixedRows = rows.slice(0, 3);
    const tail = rows.slice(3);
    this.pages = this.chunk(tail, LiveResultsTableBodyComponent.PAGE_SIZE);
  }

  private chunk<T>(arr: T[], size: number): T[][] {
    const out: T[][] = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  }

  public pageId(i: number) { return `page-${i}`; }

  protected readonly ResultProcessingType = ResultProcessingType;

}

export class LiveResultTableRowData {
  constructor(public delta?: number, public updated?: boolean, public id?: number, public competitorResults?: CompetitorResults) { }
}
